import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaLayerGroup }     from '@react-icons/all-files/fa/FaLayerGroup';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog }            from '@react-icons/all-files/fa/FaCog';
import { FaHome }           from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageForm, { FormField } from '../../../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntity, createEntity, updateEntity, reset } from '../../../../manager/workflow-clase.reducer';

const BACK = '/configuracion/workflow/clase';

interface ClaseForm {
  id: string;
  nombre: string;
}

const empty: ClaseForm = { id: '', nombre: '' };

const fields: FormField<ClaseForm>[] = [
  {
    name:     'id',
    label:    'ID',
    readOnly: true,
    editOnly: true,
  },
  {
    name:        'nombre',
    label:       'Nombre',
    required:    true,
    placeholder: 'Nombre de la clase',
  },
];

export default function ClaseFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew  = !id;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const entity        = useAppSelector(s => s.workflowClase.entity) as any;
  const updating      = useAppSelector(s => s.workflowClase.updating);
  const updateSuccess = useAppSelector(s => s.workflowClase.updateSuccess);

  const [form, setForm]     = useState<ClaseForm>(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof ClaseForm, string>>>({});

  useEffect(() => {
    if (!isNew) {
      dispatch(getEntity(id!));
    } else {
      dispatch(reset());
      setForm(empty);
    }
  }, [id]);

  useEffect(() => {
    if (!isNew && entity?.id) {
      setForm({
        id:     String(entity.id),
        nombre: entity.nombre ?? '',
      });
    }
  }, [entity]);

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      navigate(BACK, {
        state: { successMsg: isNew ? 'Clase creada correctamente.' : 'Clase actualizada correctamente.' },
      });
    }
  }, [updateSuccess]);

  const handleFieldChange = (name: keyof ClaseForm, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const e2: Partial<Record<keyof ClaseForm, string>> = {};
    if (!form.nombre.trim()) e2.nombre = 'El nombre es requerido';
    if (Object.keys(e2).length) { setErrors(e2); return; }

    const payload = {
      ...(isNew ? {} : { id: Number(form.id) }),
      nombre: form.nombre,
    };
    if (isNew) {
      dispatch(createEntity(payload));
    } else {
      dispatch(updateEntity(payload));
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <Breadcrumb items={[
        { label: 'Inicio',        to: '/',                       icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion',          icon: <FaCog /> },
        { label: 'Workflow',      to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Clase',         to: BACK,                      icon: <FaLayerGroup /> },
        { label: isNew ? 'Nueva Clase' : `Editar Clase #${id}` },
      ]} />

      <PageHeader
        icon={<FaLayerGroup />}
        iconColor="#6366f1"
        title={isNew ? 'Nueva Clase' : 'Editar Clase'}
        subtitle={isNew
          ? 'Completa los campos para crear una nueva clase'
          : `Modificando clase #${id}`}
      />

      <PageForm
        fields={fields}
        values={form}
        errors={errors}
        isNew={isNew}
        saving={updating}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={() => navigate(BACK)}
        accentColor="#6366f1"
      />
    </div>
  );
}
